import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Store, ArrowUpRight, Gavel, Tag } from "lucide-react";
import { toast } from "sonner";

function fmtUSD(n) {
  if (!n) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n}`;
}

export default function Marketplace() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [type, setType] = useState("");
  const [ipType, setIpType] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (type) params.listing_type = type;
      if (ipType) params.ip_type = ipType;
      const { data } = await api.get("/marketplace/listings", { params });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type, ipType]);

  return (
    <div className="p-6 md:p-8 fade-in-up" data-testid="marketplace-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="eyebrow">Marketplace</div>
          <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight mt-1">
            License or Buy IP
          </h1>
          <p className="text-slate-600 mt-2 text-sm md:text-base">
            Browse open listings. Place bids or offers on any asset — escrow is held automatically until deal close.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={type} onChange={(e) => setType(e.target.value)} data-testid="market-type" className="border border-slate-300 rounded-sm text-sm px-3 py-2 bg-white">
            <option value="">All listings</option>
            <option value="sale">Sale</option>
            <option value="license">License</option>
          </select>
          <select value={ipType} onChange={(e) => setIpType(e.target.value)} data-testid="market-ip-type" className="border border-slate-300 rounded-sm text-sm px-3 py-2 bg-white">
            <option value="">All IP types</option>
            <option value="patent">Patents</option>
            <option value="trademark">Trademarks</option>
            <option value="copyright">Copyrights</option>
          </select>
        </div>
      </div>

      {loading && <div className="text-slate-500 eyebrow">Loading listings…</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {rows.map((l) => (
          <button
            key={l.id}
            onClick={() => nav(`/marketplace/${l.id}`)}
            data-testid={`listing-card-${l.id}`}
            className="bengal-card p-5 text-left hover:shadow-md hover:border-slate-300 transition-all group"
          >
            <div className="flex items-center justify-between mb-3">
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border flex items-center gap-1 ${
                l.listing_type === "sale"
                  ? "bg-red-50 text-[#7A1F2B] border-red-200"
                  : "bg-amber-50 text-amber-800 border-amber-200"
              }`}>
                {l.listing_type === "sale" ? <Tag size={10} /> : <Gavel size={10} />}
                {l.listing_type}
              </span>
              <span className="eyebrow font-mono text-slate-500">{l.asset_country}</span>
            </div>
            <h3 className="font-serif text-xl font-medium tracking-tight line-clamp-2 min-h-[3.25rem]">{l.asset_title}</h3>
            <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 capitalize">
              <span>{l.asset_type}</span><span>·</span><span>{l.asset_category}</span>
            </div>
            <p className="text-sm text-slate-600 mt-3 line-clamp-2">{l.description}</p>
            <div className="flex items-end justify-between mt-5 pt-4 border-t border-slate-100">
              <div>
                <div className="eyebrow text-[9px]">Asking</div>
                <div className="font-mono text-xl text-[#B33A3A] font-semibold">{fmtUSD(l.asking_price_usd)}</div>
              </div>
              <div className="text-xs text-slate-500 flex items-center gap-1 group-hover:text-[#B33A3A] transition-colors">
                {l.bid_count} bid{l.bid_count !== 1 ? "s" : ""}
                <ArrowUpRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
              </div>
            </div>
          </button>
        ))}
      </div>

      {!loading && rows.length === 0 && (
        <div className="bengal-card p-12 text-center">
          <Store size={32} className="mx-auto text-slate-300 mb-3" />
          <div className="text-slate-500 text-sm">No open listings match these filters.</div>
        </div>
      )}
    </div>
  );
}
